'use client';

import { Portal, Spinner } from '@common/components';
import { BREAKPOINTS, useBodyScrollLock } from '@common/hooks';
import { cn } from '@core/utils';
import { usePathname } from 'next/navigation';
import { useState } from 'react';

import { getMainWrapperElement } from '@/app/_components/MainWrapperPortal';
import { DesktopSkeletonCover } from '@/components/organism/DesktopSkeletonCover';
import { MobileSkeletonCover } from '@/components/organism/MoblieSkeletonCover';
import { useMediaQuery } from '@/hooks/useMediaQuery';

interface LoadingPageTemplateProps {
  blur?: boolean;
  fadeIn?: boolean;
  unmountedPlaceholder?: boolean;
  className?: string;
}

export const LoadingPageTemplate = ({
  blur = false,
  fadeIn,
  unmountedPlaceholder,
  className,
}: LoadingPageTemplateProps) => {
  useBodyScrollLock(true);

  const pathname = usePathname();
  const isDesktopViewport = useMediaQuery(BREAKPOINTS.DESKTOP, true);

  const [isMounted, setIsMounted] = useState(false);

  const type =
    (pathname?.startsWith('/donation') ?? false) && isDesktopViewport ? 'full' : 'wello';

  return (
    <>
      {unmountedPlaceholder && !isMounted ? (
        <Loading
          type={type}
          blur={blur}
          fadeIn={fadeIn}
          className={className}
        />
      ) : null}
      <Portal
        renderTo={getMainWrapperElement}
        onLoaded={() => setIsMounted(true)}
      >
        <Loading
          type={type}
          blur={blur}
          fadeIn={fadeIn}
          className={className}
        />
      </Portal>
    </>
  );
};

const Loading = ({
  type,
  fadeIn,
  className,
  blur,
}: LoadingPageTemplateProps & { type: 'full' | 'wello' }) => {
  return (
    <div
      className={cn(
        'fixed inset-0 z-40 flex overflow-hidden',
        {
          'animate-fade duration-300 animate-delay-200': fadeIn,
          'z-50': blur,
        },
        className,
      )}
    >
      {blur ? (
        // Blur
        <div className="absolute z-10 size-full bg-Gray-black/60" />
      ) : type === 'full' ? (
        <DesktopSkeletonCover />
      ) : (
        <MobileSkeletonCover />
      )}

      {/* Spinner */}
      <div className="absolute z-30 size-full flex-center">
        <Spinner logo color="white" size={80} />
      </div>
    </div>
  );
};
